import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../contexts/AppContext';
import { useFormContext } from '../contexts/FormContext';
import { Server } from '../services/Server';
import type { Project } from '../types/interfaces';
import { handleError } from '../utils/Error';
import { getSoilingBlockKeys } from '../utils/soilingSensor';
import { cleanFormDataForSave, cleanUserPatterns } from '../utils/utils';

interface UseProjectMutationsArgs {
  projectId?: string;
  project: Project | undefined;
  isValidForm: () => boolean;
}

export const useProjectMutations = ({ projectId, project, isValidForm }: UseProjectMutationsArgs) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { setModified } = useAppContext();
  const { getValues, getFormDataItem, results } = useFormContext();
  const { copyProjectAPI, postProjectAPI, putProjectAPI } = Server();
  const [isSaving, setIsSaving] = useState(false);
  const [preservedPatternData, setPreservedPatternData] = useState<Record<string, unknown>>({});

  const createMutation = useMutation({
    mutationFn: async (data: object) => {
      const cleanedData = cleanUserPatterns(data);
      setIsSaving(true);

      return projectId
        ? putProjectAPI(projectId, { ...cleanedData, results: { ...results } })
        : postProjectAPI({ ...cleanedData, results: { ...results } });
    },
    onSuccess: (data) => {
      if (projectId) {
        // Keep only the expensive soiling-ratios charts cached (they refetch on their own input
        // changes; a blanket invalidate made them reload on every save, e.g. the combine autosave).
        // Everything else — including the yield-evaluation chart, whose key doesn't encode the
        // monthly values — must refresh after a save so edits show without a manual refresh.
        queryClient.invalidateQueries({
          predicate: (query) => {
            const key = query.queryKey ?? [];
            const isSoilingRatios = key[0] === 'smt-chart-soiling-ratios' || key[1] === 'soiling-ratios';
            return !isSoilingRatios && key[0] !== 'rain-recommendation';
          },
        });
      } else if (data) {
        navigate('/project/' + data);
      }
      setIsSaving(false);
    },
    onError: (error: unknown) => {
      handleError(error);
      setIsSaving(false);
      setModified(false);
    },
  });

  const handleSave = () => {
    // A project the caller can only read is never saved; copy it to get an editable one.
    if (project?.can_edit === false) {
      setModified(false);
      return;
    }
    if (isValidForm()) {
      const formData = getValues();

      const patternData: Record<string, unknown> = {};
      getSoilingBlockKeys(formData).forEach((key) => {
        const pattern = getFormDataItem(formData, `${key}.pattern`);
        if (pattern) {
          patternData[key] = pattern;
        }
      });
      setPreservedPatternData(patternData);

      createMutation.mutate(cleanFormDataForSave(formData));
    } else {
      setModified(false);
    }
  };

  const handleDuplicate = async () => {
    try {
      setIsSaving(true);
      const { id, name } = project!;
      const newProjectId = await copyProjectAPI(id, `${name} - Copy ${new Date().toLocaleString()}`);
      toast.success('Project successfully duplicated');
      if (newProjectId) {
        navigate('/project/' + newProjectId);
      }
    } catch (err) {
      console.log(err);
      toast.error(`There was an error while trying to duplicate this project: ${err}`);
    } finally {
      setIsSaving(false);
    }
  };

  return { isSaving, preservedPatternData, handleSave, handleDuplicate };
};
